<template>
  <section
    ref="wrapRef"
    class="wh-country-chart rounded-2xl border border-gray-modern-100 bg-white p-6 shadow-sm"
    role="region"
    :aria-label="ariaLabel"
  >
    <div class="min-w-0 shrink">
      <div class="flex min-w-0 items-start justify-between gap-3">
        <div class="min-w-0 flex-1 flex flex-col gap-1">
          <h3 class="wh-country-chart__title">{{ title }}</h3>
          <p class="wh-country-chart__subtitle">{{ subtitle }}</p>
        </div>
        <ChartImageDownloadButton
          v-if="downloadable"
          :disabled="!canDownload"
          :busy="isDownloading"
          @click="downloadImage"
        />
      </div>

      <ul
        class="wh-country-chart__legend mt-[24px] m-0 flex list-none flex-wrap items-center justify-end gap-x-6 gap-y-2 p-0"
      >
        <li class="wh-country-chart__legend-item">
          <span class="wh-country-chart__legend-dot" :style="{ background: COLOR_WOMEN_REG }" />
          {{ legendWomenRegular }}
        </li>
        <li class="wh-country-chart__legend-item">
          <span class="wh-country-chart__legend-dot" :style="{ background: COLOR_WOMEN_OT }" />
          {{ legendWomenOvertime }}
        </li>
        <li class="wh-country-chart__legend-item">
          <span class="wh-country-chart__legend-dot" :style="{ background: COLOR_MEN_REG }" />
          {{ legendMenRegular }}
        </li>
        <li class="wh-country-chart__legend-item">
          <span class="wh-country-chart__legend-dot" :style="{ background: COLOR_MEN_OT }" />
          {{ legendMenOvertime }}
        </li>
      </ul>

      <div
        ref="chartRef"
        data-chart-scroll-plot
        class="wh-country-chart__plot mt-[20px] min-h-0 min-w-0 w-full overflow-x-auto [-webkit-overflow-scrolling:touch]"
      >
        <p v-if="!hasChartData" class="wh-country-chart__empty m-0 py-6 text-center">
          {{ emptyCopy }}
        </p>
        <svg
          v-else
          class="block h-auto max-w-none shrink-0"
          :width="svgW"
          :height="svgH"
          :viewBox="`0 0 ${svgW} ${svgH}`"
          preserveAspectRatio="xMinYMid meet"
          aria-hidden="true"
        >
          <!-- Rows: label + stacked bar -->
          <g v-for="(row, ri) in barRows" :key="'r' + ri">
            <text
              class="wh-country-chart__row-label"
              :x="plotArea.labelRight - 8"
              :y="row.cy + 4"
              text-anchor="end"
            >
              {{ row.label }}
            </text>
            <path v-if="row.regPath" :d="row.regPath" :fill="row.regColor" />
            <path v-if="row.otPath" :d="row.otPath" :fill="row.otColor" />
            <rect
              v-if="row.regTooltip && row.regHit"
              class="wh-country-chart__hit-target"
              :x="row.regHit.x"
              :y="row.regHit.y"
              :width="row.regHit.w"
              :height="row.regHit.h"
              fill="transparent"
              @mouseenter="showTip($event, row.regTooltip)"
              @mousemove="moveTip"
              @mouseleave="hideTip"
            />
            <rect
              v-if="row.otTooltip && row.otHit"
              class="wh-country-chart__hit-target"
              :x="row.otHit.x"
              :y="row.otHit.y"
              :width="row.otHit.w"
              :height="row.otHit.h"
              fill="transparent"
              @mouseenter="showTip($event, row.otTooltip)"
              @mousemove="moveTip"
              @mouseleave="hideTip"
            />
            <text
              v-if="row.regLabel"
              :x="row.regLabelX"
              :y="row.cy + 4"
              text-anchor="middle"
              class="wh-country-chart__bar-label"
            >
              {{ row.regLabel }}
            </text>
            <text
              v-if="row.otLabel"
              :x="row.otLabelX"
              :y="row.cy + 4"
              text-anchor="middle"
              class="wh-country-chart__bar-label wh-country-chart__bar-label--ot"
            >
              {{ row.otLabel }}
            </text>
          </g>

          <!-- X axis ticks -->
          <g>
            <line
              class="wh-country-chart__axis-line"
              :x1="plotArea.barLeft"
              :x2="plotArea.barRight"
              :y1="axisY"
              :y2="axisY"
            />
            <text
              v-for="(t, ti) in xTickLayout"
              :key="'xt' + ti"
              class="wh-country-chart__x-tick"
              :x="t.x"
              :y="axisY + 18"
              text-anchor="middle"
            >
              {{ t.label }}
            </text>
          </g>
        </svg>
      </div>
    </div>
    <ChartHoverTooltip :tip="tip" />
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import ChartHoverTooltip from "./ChartHoverTooltip.vue";
import ChartImageDownloadButton from "./ChartImageDownloadButton.vue";
import { useChartImageDownload } from "../utils/useChartImageDownload";
import {
  horizontalHitArea,
  useChartHoverTooltip,
} from "../utils/useChartHoverTooltip";

/** Design palette (CSS variables with hex fallbacks match gender dashboard chart tokens) */
const COLOR_WOMEN_REG = "var(--Teal-400, #2ED3B7)";
const COLOR_WOMEN_OT = "var(--Teal-200, #99F6E0)";
const COLOR_MEN_REG = "var(--Indigo-500, #6172F3)";
const COLOR_MEN_OT = "var(--Indigo-200, #C7D7FE)";

const X_TICK_STEP = 20;
/** Minimum bar segment width (px) before an inline % label is shown. */
const MIN_SEGMENT_W_FOR_LABEL = 28;

const props = defineProps({
  title: { type: String, default: "Regular and overtime hours by country" },
  subtitle: {
    type: String,
    default: "Average monthly hours as % of legal maximum, by gender",
  },
  legendWomenRegular: { type: String, default: "Women: Regular hours" },
  legendWomenOvertime: { type: String, default: "Women: Overtime" },
  legendMenRegular: { type: String, default: "Men: Regular hours" },
  legendMenOvertime: { type: String, default: "Men: Overtime" },
  womenRowSuffix: { type: String, default: "women" },
  menRowSuffix: { type: String, default: "men" },
  rowsJson: { type: String, default: "" },
  emptyCopy: {
    type: String,
    default: "No working hours data available for the selected portfolio.",
  },
  downloadable: { type: Boolean, default: false },
  downloadContext: { type: String, default: "" },
});

const ariaLabel = computed(() => {
  const rows = flatRows.value;
  if (!rows.length) return `${props.title}. ${props.emptyCopy}`;
  const parts = rows.map(
    (row) =>
      `${row.label}: ${formatPct(row.regular)} regular, ${formatPct(row.overtime)} overtime`,
  );
  return `${props.title}. ${props.subtitle}. ${parts.join("; ")}`;
});

const wrapRef = ref(null);
const chartRef = ref(null);
const measuredW = ref(720);
const { tip, show: showTip, hide: hideTip, move: moveTip } = useChartHoverTooltip();

const margin = { top: 16, right: 24, bottom: 44, left: 0 };

const countryGroups = computed(() => {
  const s = props.rowsJson;
  if (typeof s !== "string" || !s.trim()) return [];

  try {
    const data = JSON.parse(s);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
});

const hasChartData = computed(() => countryGroups.value.length > 0);

const { downloadImage, isDownloading, canDownload } = useChartImageDownload({
  getRootElement: () => wrapRef.value,
  title: () => props.title,
  downloadContext: () => props.downloadContext,
  hasData: hasChartData,
});

/** One object per country from the server: worker-weighted % of legal full-time annual hours. */
const flatRows = computed(() => {
  const rows = [];
  for (const g of countryGroups.value) {
    const country = typeof g.country === "string" ? g.country : "";
    rows.push({
      label: `${country} (${props.womenRowSuffix})`,
      gender: "women",
      regular: pctField(g, "womenRegular"),
      overtime: pctField(g, "womenOvertime"),
    });
    rows.push({
      label: `${country} (${props.menRowSuffix})`,
      gender: "men",
      regular: pctField(g, "menRegular"),
      overtime: pctField(g, "menOvertime"),
    });
  }
  return rows;
});

function pctField(row, camelKey) {
  const snake = camelKey.replace(/([A-Z])/g, "_$1").toLowerCase();
  const raw = row[camelKey] ?? row[snake];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function formatPct(value) {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toFixed(1)}%`;
}

function segmentTooltip(label, pct) {
  return pct > 0 ? `${label}: ${formatPct(pct)}` : "";
}

/** Data-driven x-axis max so bars use the plot width without anchoring at 100% legal max. */
function xAxisFromRows(rows) {
  let peak = 0;
  for (const row of rows) {
    peak = Math.max(peak, Math.max(0, row.regular) + Math.max(0, row.overtime));
  }
  if (peak <= 0) return { max: 100, tickStep: X_TICK_STEP };

  const target = peak * 1.1;
  const tickStep = target <= 60 ? 10 : target <= 120 ? X_TICK_STEP : 40;
  let max = Math.ceil(target / tickStep) * tickStep;
  if (max < peak) max += tickStep;
  return { max, tickStep };
}

/**
 * Horizontal segment [left, right] vertically centered at cy with height barH.
 * When roundRight > 0, only the trailing (right) corners are rounded.
 */
function stackedSegPath(left, right, cy, barH, roundRight) {
  if (!(right - left > 0.01)) return "";
  const half = barH / 2;
  const lo = cy - half;
  const hi = cy + half;
  const R = Math.min(roundRight, (right - left) / 2, half);
  if (R < 0.5) {
    return `M ${left} ${lo} L ${right} ${lo} L ${right} ${hi} L ${left} ${hi} Z`;
  }
  const innerR = right - R;
  return [
    `M ${left} ${lo}`,
    `L ${innerR} ${lo}`,
    `A ${R} ${R} 0 0 1 ${right} ${lo + R}`,
    `L ${right} ${hi - R}`,
    `A ${R} ${R} 0 0 1 ${innerR} ${hi}`,
    `L ${left} ${hi}`,
    "Z",
  ].join(" ");
}

const LABEL_COL_MIN = 128;
const BAR_AREA_MIN_WIDTH = 400;

/** Horizontal bar thickness (px, vertical extent in the chart). */
const BAR_THICKNESS = 16;
/** Gap between women’s and men’s bar for the same country / facility (px). */
const GAP_WOMEN_MEN = 4;
/** Vertical gap between one facility’s bar block and the next (men’s bar bottom → next women’s bar top, px). */
const GAP_BETWEEN_FACILITIES = 16;

const nRows = computed(() => flatRows.value.length);

const labelColW = computed(() => LABEL_COL_MIN);

const plotInnerBarW = computed(() =>
  Math.max(BAR_AREA_MIN_WIDTH, measuredW.value - margin.left - margin.right - labelColW.value),
);

const svgW = computed(
  () => margin.left + labelColW.value + plotInnerBarW.value + margin.right,
);

const plotTop = computed(() => margin.top);

/** Vertical center (cy) per row: women→men spacing `GAP_WOMEN_MEN`, then `GAP_BETWEEN_FACILITIES` before the next facility. */
const rowCentersY = computed(() => {
  const rows = flatRows.value;
  const centers = [];
  const stepWomenToMen = BAR_THICKNESS + GAP_WOMEN_MEN;
  const stepMenToNextWomen = BAR_THICKNESS + GAP_BETWEEN_FACILITIES;
  let y = plotTop.value + BAR_THICKNESS / 2;
  for (let i = 0; i < rows.length; i++) {
    centers.push(y);
    if (i < rows.length - 1) {
      const step = rows[i].gender === "women" ? stepWomenToMen : stepMenToNextWomen;
      y += step;
    }
  }
  return centers;
});

const lastBarBottomY = computed(() => {
  const centers = rowCentersY.value;
  if (!centers.length) return plotTop.value + BAR_THICKNESS;
  const last = centers[centers.length - 1];
  return last + BAR_THICKNESS / 2;
});

const axisY = computed(() => lastBarBottomY.value + 10);

const svgH = computed(() => axisY.value + margin.bottom);

const plotArea = computed(() => ({
  labelRight: margin.left + labelColW.value,
  barLeft: margin.left + labelColW.value + 12,
  barRight: margin.left + labelColW.value + 12 + plotInnerBarW.value,
  top: plotTop.value,
  bottom: axisY.value - 4,
}));

const xScaleConfig = computed(() => xAxisFromRows(flatRows.value));

function xToPx(pctValue) {
  const { barLeft, barRight } = plotArea.value;
  const span = barRight - barLeft;
  const max = xScaleConfig.value.max;
  if (!(max > 0)) return barLeft;
  return barLeft + (Math.max(0, pctValue) / max) * span;
}

const xTickLayout = computed(() => {
  const { max, tickStep } = xScaleConfig.value;
  const ticks = [];
  for (let v = 0; v <= max + 1e-6; v += tickStep) {
    ticks.push({ x: xToPx(v), label: `${Math.round(v)}%` });
  }
  return ticks;
});

const barRows = computed(() => {
  const cornerR = Math.min(4, BAR_THICKNESS / 2);
  const centers = rowCentersY.value;
  return flatRows.value.map((row, i) => {
    const cy = centers[i] ?? plotTop.value + BAR_THICKNESS / 2;
    const reg = Math.max(0, row.regular);
    const ot = Math.max(0, row.overtime);
    const x0 = xToPx(0);
    const xR = xToPx(reg);
    const xOt = xToPx(reg + ot);
    const barH = BAR_THICKNESS;
    const regW = xR - x0;
    const otW = xOt - xR;
    const hasReg = reg > 0 && regW > 0.5;
    const hasOt = ot > 0 && otW > 0.5;
    const regColor = row.gender === "women" ? COLOR_WOMEN_REG : COLOR_MEN_REG;
    const otColor = row.gender === "women" ? COLOR_WOMEN_OT : COLOR_MEN_OT;
    const regLegend =
      row.gender === "women" ? props.legendWomenRegular : props.legendMenRegular;
    const otLegend =
      row.gender === "women" ? props.legendWomenOvertime : props.legendMenOvertime;

    let regPath = "";
    let otPath = "";
    if (hasReg && hasOt) {
      regPath = stackedSegPath(x0, xR, cy, barH, 0);
      otPath = stackedSegPath(xR, xOt, cy, barH, cornerR);
    } else if (hasReg) {
      regPath = stackedSegPath(x0, xR, cy, barH, cornerR);
    } else if (hasOt) {
      otPath = stackedSegPath(x0, xOt, cy, barH, cornerR);
    }

    const regLabel =
      hasReg && regW >= MIN_SEGMENT_W_FOR_LABEL ? formatPct(reg) : "";
    const otLabel = hasOt && otW >= MIN_SEGMENT_W_FOR_LABEL ? formatPct(ot) : "";

    return {
      ...row,
      cy,
      regW,
      otW,
      regPath,
      otPath,
      regColor,
      otColor,
      regTooltip: segmentTooltip(regLegend, reg),
      otTooltip: segmentTooltip(otLegend, ot),
      regHit: hasReg ? horizontalHitArea(x0, cy - barH / 2, regW, barH) : null,
      otHit: hasOt ? horizontalHitArea(xR, cy - barH / 2, otW, barH) : null,
      regLabel,
      otLabel,
      regLabelX: x0 + regW / 2,
      otLabelX: xR + otW / 2,
    };
  });
});

let resizeObserver;

onMounted(() => {
  const measure = () => {
    const cw = chartRef.value?.clientWidth;
    const ww = wrapRef.value?.clientWidth;
    const wd = cw && cw > 0 ? cw : ww && ww > 0 ? ww : 720;
    measuredW.value = Math.floor(wd);
  };
  nextTick(() => {
    measure();
    resizeObserver = new ResizeObserver(measure);
    if (chartRef.value) resizeObserver.observe(chartRef.value);
  });
});

onBeforeUnmount(() => resizeObserver?.disconnect());
</script>

<style scoped>
.wh-country-chart__title {
  margin: 0;
  color: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.wh-country-chart__subtitle {
  margin: 0;
  color: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

/* Flex + gap in scoped CSS so legends lay out correctly in ActiveAdmin (admin
   Tailwind build may not include every utility class used only in .vue files). */
.wh-country-chart__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 24px 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.wh-country-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  line-height: normal;
}

.wh-country-chart__legend-dot {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 999px;
}

.wh-country-chart__row-label {
  fill: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
}

.wh-country-chart__axis-line {
  stroke: #e5e7eb;
  stroke-width: 1;
}

.wh-country-chart__x-tick {
  fill: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  font-weight: 400;
}

.wh-country-chart__empty {
  color: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.wh-country-chart__bar-label {
  fill: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-size: 10px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.wh-country-chart__bar-label--ot {
  fill: #374151;
}

.wh-country-chart__hit-target {
  cursor: default;
}
</style>
